import React, { useState, useEffect, useRef } from 'react';
import Alert from './Alert';
import apiService from '../../services/ApiService';
import RegionalService from '../../services/RegionalService';
import './ChatModal.css';

const ChatModal = ({ isOpen, onClose, assistant, publicChat = false, regionalId, areaId, password, headerTitle }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showAudioToggle, setShowAudioToggle] = useState(false);
  const [audioReplyEnabled, setAudioReplyEnabled] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto scroll al final cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inicializar chat cuando se abre el modal
  useEffect(() => {
    if (isOpen && assistant) {
      const assistantFlags = assistant?.featureFlags || assistant?.FeatureFlags || [];
      const flags = assistantFlags.length > 0 ? assistantFlags : RegionalService.getFeatureFlags();
      const audioFlag = flags.find((flag) => {
        const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
        return nombre === 'permiterespuestaaudiochat';
      });
      const isAudioEnabled = Boolean(audioFlag?.activo ?? audioFlag?.Activo);
      setShowAudioToggle(isAudioEnabled);
      if (!isAudioEnabled) {
        setAudioReplyEnabled(false);
      }
      initializeChat();
    } else {
      // Limpiar estado cuando se cierra
      setMessages([]);
      setThreadId(null);
      setIsInitializing(true);
      setInputMessage('');
      setAlert({ show: false, message: '', type: 'error' });
      setShowAudioToggle(false);
      setAudioReplyEnabled(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assistant]);

  const initializeChat = async () => {
    try {
      setIsInitializing(true);
      setAlert({ show: false, message: '', type: 'error' });
      
      const assistantName =
        headerTitle ||
        assistant.nombreAsistente ||
        assistant.nombre ||
        assistant.Nombre ||
        assistant.displayName ||
        assistant.DisplayName ||
        assistant.email ||
        assistant.Email ||
        'Asistente';

      const assistantId = assistant.idOpenAIAssistant || assistant.idAssistant || assistant.id;
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para iniciar la conversación');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para iniciar la conversación');
      }

      if (publicChat) {
        const response = await apiService.postPublic('/Asistente/publico/threads', {
          idOpenAIAssistant: assistantId,
          regionalId,
          areaId,
          password
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      } else {
        const response = await apiService.post('/Threads/threads', {
          idOpenAIAssistant: assistantId
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      }

      // Agregar mensaje de bienvenida
      const welcomeMessage = {
        id: 'welcome-' + Date.now(),
        content: `Conversación iniciada con el asistente ${assistantName}`,
        role: 'system',
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
      
    } catch (error) {
      console.error('❌ Error al inicializar chat:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al inicializar la conversación',
        type: 'error'
      });
    } finally {
      setIsInitializing(false);
    }
  };

  // Función para extraer HTML del bloque de código markdown
  const extractHtmlFromMarkdown = (content) => {
    
    // Si el contenido viene envuelto en ```html ... ```
    const htmlBlockMatch = content.match(/```html\s*\n([\s\S]*?)\n```/);
    if (htmlBlockMatch) {
      const htmlContent = htmlBlockMatch[1];
      return extractBodyContent(htmlContent);
    }
    
    // Si viene envuelto en ``` ... ``` (sin especificar html)
    const genericBlockMatch = content.match(/```\s*\n([\s\S]*?)\n```/);
    if (genericBlockMatch) {
      const innerContent = genericBlockMatch[1];
      // Verificar si parece HTML
      if (innerContent.includes('<html>') || innerContent.includes('<!DOCTYPE')) {
        return extractBodyContent(innerContent);
      }
    }
    
    // Si no hay bloque de código pero parece HTML completo
    if (content.includes('<!DOCTYPE') || content.includes('<html>')) {
      return extractBodyContent(content);
    }
    
    return content;
  };

  // Función para extraer solo el contenido del body de un HTML completo
  const extractBodyContent = (htmlString) => {
    // Buscar el contenido dentro de las etiquetas <body>
    const bodyMatch = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
    
    // Si no hay body, pero hay html, buscar después del head
    const afterHeadMatch = htmlString.match(/<\/head>\s*([\s\S]*?)<\/html>/i);
    if (afterHeadMatch) {
      return afterHeadMatch[1].trim();
    }
    
    // Si no encuentra estructura HTML completa, retornar tal como está
    return htmlString;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !threadId || isLoading) return;

    const userMessage = {
      id: 'user-' + Date.now(),
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };

    const messageToSend = inputMessage.trim();
    
    // Agregar mensaje del usuario inmediatamente
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      
      // Llamada real a la API
      const assistantId = assistant.idOpenAIAssistant || assistant.idAssistant || assistant.id;
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para enviar el mensaje');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para enviar el mensaje');
      }

      const response = publicChat
        ? await apiService.postPublic('/Asistente/publico/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            regionalId,
            areaId,
            password,
            permiteRespuestaAudioChat: audioReplyEnabled
          })
        : await apiService.post('/Asistente/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            permiteRespuestaAudioChat: audioReplyEnabled
          });


      if (response.ok && response.respuesta) {
        // Extraer HTML del bloque de código markdown si es necesario
        const htmlContent = extractHtmlFromMarkdown(response.respuesta);
        const archivoIds = response.archivoIds || response.ArchivoIds || [];
        const archivoLinks = response.archivoLinks || response.ArchivoLinks || [];
        const hasServerLinks = Array.isArray(archivoLinks) && archivoLinks.length > 0;
        const shouldStripLinks = hasServerLinks || /\/mnt\/data\//i.test(htmlContent);
        const safeHtmlContent = shouldStripLinks
          ? htmlContent.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
          : htmlContent;
        const downloadLinks = (Array.isArray(archivoLinks) && archivoLinks.length > 0)
          ? `
            <div class="download-box">
              <p><strong>Archivos generados</strong></p>
              <ul>
                ${archivoLinks.map((href, idx) => {
                  return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                }).join('')}
              </ul>
            </div>
          `
          : (Array.isArray(archivoIds) && archivoIds.length > 0
            ? `
              <div class="download-box">
                <p><strong>Archivos generados</strong></p>
                <ul>
                  ${archivoIds.map((fileId, idx) => {
                    const href = publicChat
                      ? `${apiService.baseURL}/Asistente/publico/archivos/${fileId}/download?regionalId=${regionalId}`
                      : `${apiService.baseURL}/Asistente/archivos/${fileId}/download`;
                    return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                  }).join('')}
                </ul>
              </div>
            `
            : '');
        const audioBase64 = response.audioBase64 || response.AudioBase64;
        const audioContentType = response.audioContentType || response.AudioContentType || 'audio/mpeg';
        const audioSrc = audioBase64 ? `data:${audioContentType};base64,${audioBase64}` : '';
        const audioBlock = audioSrc
          ? `
            <div class="download-box audio-box">
              <p><strong>Respuesta en audio</strong></p>
              <audio controls src="${audioSrc}"></audio>
              <a href="${audioSrc}" download="respuesta.mp3">Descargar audio</a>
            </div>
          `
          : '';
        const finalContent = `${safeHtmlContent}${downloadLinks}${audioBlock}`;
        
        const assistantMessage = {
          id: response.idMensaje || 'assistant-' + Date.now(),
          content: finalContent,
          role: 'assistant',
          timestamp: new Date(response.fechaRespuesta || new Date())
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
      } else {
        throw new Error(response.error || 'Error al procesar la respuesta del asistente');
      }
      
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);
      
      // Mostrar mensaje de error en el chat
      const errorMessage = {
        id: 'error-' + Date.now(),
        content: `<div class="error-message">
          <p><strong>❌ Error al enviar mensaje</strong></p>
          <p>Lo siento, no pude procesar tu mensaje. Por favor, inténtalo nuevamente.</p>
          <p><em>Detalles: ${error.message}</em></p>
        </div>`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      // También mostrar alerta
      setAlert({
        show: true,
        message: `Error al enviar mensaje: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para sanitizar HTML básico (opcional, para mayor seguridad)
  const sanitizeHtml = (html) => {
    // Lista básica de tags permitidos para mensajes del asistente
    // eslint-disable-next-line no-unused-vars
    const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 
                        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 
                        'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'hr', 'mark', 'div'];
    
    // Por ahora retornamos el HTML tal como viene
    // En producción podrías usar una librería como DOMPurify para filtrar por allowedTags
    return html;
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  if (!isOpen) return null;

  return (
    <div className="chat-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`chat-modal${publicChat ? ' public-chat' : ''}`}>
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="assistant-avatar">
              <i className="fas fa-robot"></i>
            </div>
            <div className="assistant-info">
              <h3 className="assistant-name">
                {headerTitle || assistant?.nombreAsistente || assistant?.nombre || assistant?.Nombre || 'Asistente IA'}
              </h3>
              <p className="assistant-status">
                {isInitializing ? 'Inicializando...' : 
                 threadId ? 'Conectado' : 'Sin conexión'}
              </p>
            </div>
          </div>
          
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Alert */}
        {alert.show && (
          <div className="chat-alert">
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          </div>
        )}

        {/* Messages */}
        <div className="chat-messages">
          {isInitializing ? (
            <div className="initializing-message">
              <div className="thinking-content">
                <div className="thinking-avatar">
                  <i className="fas fa-cog fa-spin"></i>
                </div>
                <div className="thinking-text">
                  <span className="thinking-label">
                    Inicializando conversación con {headerTitle || assistant?.nombreAsistente || assistant?.nombre || assistant?.Nombre || 'Asistente'}...
                  </span>
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.role === 'user' ? 'user-message' : 
                             message.role === 'system' ? 'system-message' : 'assistant-message'}`}
                >
                  <div className="message-content">
                    <div 
                      className="message-text"
                      dangerouslySetInnerHTML={{ 
                        __html: message.role === 'assistant' 
                          ? sanitizeHtml(message.content) 
                          : message.content.replace(/\n/g, '<br>')
                      }}
                    />
                    {message.timestamp && (
                      <div className="message-time">
                        {formatTime(message.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="message assistant-message thinking-message">
                  <div className="message-content">
                    <div className="message-text thinking-bubble">
                      <div className="thinking-content">
                        <div className="thinking-avatar">
                          <i className="fas fa-robot"></i>
                        </div>
                        <div className="thinking-text">
                          <span className="thinking-label">
                            {headerTitle || assistant?.nombreAsistente || assistant?.nombre || assistant?.Nombre || 'Asistente'} está escribiendo
                          </span>
                          <div className="typing-indicator">
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="chat-input-area">
          {showAudioToggle && (
            <div className="chat-options">
              <label className="audio-toggle">
                <input
                  type="checkbox"
                  checked={audioReplyEnabled}
                  onChange={(e) => setAudioReplyEnabled(e.target.checked)}
                />
                <span>Respuesta en forma de audio</span>
              </label>
            </div>
          )}
          <div className="chat-input-container">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isInitializing ? "Inicializando..." : "Escribe tu mensaje..."}
              disabled={isLoading || isInitializing || !threadId}
              rows={1}
              className="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || isInitializing || !threadId}
              className="send-btn"
            >
              {isLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
